import { useEffect, useState } from 'react';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useBackgroundSync } from '@/hooks/useBackgroundSync';
import { Wifi, WifiOff, Cloud, CloudOff, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface ConnectionStatusIndicatorProps {
  className?: string;
  showDetails?: boolean;
  compact?: boolean;
}

export function ConnectionStatusIndicator({ 
  className, 
  showDetails = false,
  compact = false 
}: ConnectionStatusIndicatorProps) {
  const { isOnline, effectiveType } = useNetworkStatus();
  const { status: syncStatus, pendingCount, triggerSync } = useBackgroundSync();
  const [showOfflineBanner, setShowOfflineBanner] = useState(false);

  // Show banner when going offline
  useEffect(() => {
    if (!isOnline) {
      setShowOfflineBanner(true);
    } else {
      // Hide banner after a short delay when coming back online
      const timer = setTimeout(() => setShowOfflineBanner(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  const handleSync = async () => {
    if (isOnline && pendingCount > 0) {
      await triggerSync();
    }
  };

  if (compact) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        {isOnline ? (
          <Wifi className="h-4 w-4 text-success" />
        ) : (
          <WifiOff className="h-4 w-4 text-destructive" />
        )}
        {pendingCount > 0 && (
          <Badge variant="secondary" className="text-xs">
            {pendingCount}
          </Badge>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-3", className)}>
      {/* Connection Status */}
      <div className="flex items-center gap-2">
        {isOnline ? (
          <>
            <div className="relative">
              <Wifi className="h-4 w-4 text-success" />
              <span className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-success rounded-full animate-pulse" />
            </div>
            {showDetails && effectiveType && (
              <span className="text-xs text-muted-foreground uppercase">
                {effectiveType}
              </span>
            )}
          </>
        ) : (
          <>
            <WifiOff className="h-4 w-4 text-destructive" />
            <span className="text-xs text-destructive font-medium">
              Offline
            </span>
          </>
        )}
      </div>

      {/* Sync Status */}
      {pendingCount > 0 && (
        <div className="flex items-center gap-2">
          <CloudOff className="h-4 w-4 text-warning" />
          <Badge variant="outline" className="text-xs border-warning text-warning">
            {pendingCount} pendente{pendingCount > 1 ? 's' : ''}
          </Badge>
          
          {isOnline && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2"
              onClick={handleSync}
              disabled={syncStatus === 'syncing'}
            >
              {syncStatus === 'syncing' ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
            </Button>
          )}
        </div>
      )}

      {/* All synced indicator */}
      {pendingCount === 0 && isOnline && showDetails && (
        <div className="flex items-center gap-1 text-success">
          <Cloud className="h-4 w-4" />
          <span className="text-xs">Sincronizado</span>
        </div>
      )}
    </div>
  );
}

// Floating offline banner
export function OfflineBanner() {
  const { isOnline } = useNetworkStatus();
  const [visible, setVisible] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setVisible(true);
      setWasOffline(true);
    } else if (wasOffline) {
      // Show "back online" message briefly
      const timer = setTimeout(() => {
        setVisible(false);
        setWasOffline(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline]);

  if (!visible) return null;

  return (
    <div
      className={cn(
        "fixed top-0 left-0 right-0 z-[100] px-4 py-2 text-center text-sm font-medium transition-all duration-300",
        isOnline 
          ? "bg-success text-success-foreground" 
          : "bg-warning text-warning-foreground"
      )}
    >
      {isOnline ? (
        <div className="flex items-center justify-center gap-2">
          <Wifi className="h-4 w-4" />
          Conexão restabelecida. Sincronizando dados...
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2">
          <WifiOff className="h-4 w-4" />
          Você está offline. Os dados serão salvos localmente.
        </div>
      )}
    </div>
  );
}

export default ConnectionStatusIndicator;
